import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Loader2, AlertTriangle, LogIn, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ShiftCard } from "@/pages/star/MyAttendance";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <div className="bg-blue-900 text-white px-4 py-4 flex items-center gap-2">
      <GraduationCap className="h-6 w-6" />
      <div><p className="font-bold leading-tight">PTG Attendance</p><p className="text-xs text-blue-200">Prasad Tutor Group</p></div>
    </div>
    <div className="max-w-xl mx-auto p-4">{children}</div>
  </div>
);

export default function AttendanceScan() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);
  const load = useCallback(async () => {
    setLd(true);
    try { const r = await api.get("/teacher/attendance"); setData(r.data); } catch { /* ignore */ }
    finally { setLd(false); }
  }, []);
  useEffect(() => { if (user?.role === "teacher") load(); }, [user, load]);

  if (loading) return <Shell><div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div></Shell>;

  if (!user) return (
    <Shell>
      <Seo title="Attendance" />
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-center" data-testid="scan-login-required">
        <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-2" />
        <h1 className="text-lg font-bold text-slate-900">Teacher login required</h1>
        <p className="text-sm text-slate-600 mt-1">Only authorised PTG teachers can record attendance. Please log in with your teacher account. No attendance is saved for visitors or students.</p>
        <Button asChild className="mt-4 bg-blue-900 hover:bg-blue-950" data-testid="scan-login-btn"><Link to="/teacher/login"><LogIn className="h-4 w-4 mr-1" /> Teacher Login</Link></Button>
      </div>
    </Shell>
  );

  if (user.role !== "teacher") return (
    <Shell>
      <Seo title="Attendance" />
      <div className="rounded-2xl border border-red-300 bg-red-50 p-6 text-center" data-testid="scan-not-teacher">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-2" />
        <h1 className="text-lg font-bold text-slate-900">Attendance is for teachers only</h1>
        <p className="text-sm text-slate-600 mt-1">This account ({user.email || user.name}) is not a teacher account, so attendance cannot be recorded here.</p>
      </div>
    </Shell>
  );

  const d = data || {};
  return (
    <Shell>
      <Seo title="Attendance" />
      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4">
        <p className="text-sm text-slate-500">Signed in as</p>
        <p className="text-lg font-bold text-slate-900" data-testid="scan-teacher-name">{user.name}</p>
        <p className="text-xs text-slate-500 flex items-start gap-1 mt-1"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {d.settings?.configured ? <>Location check is ON · you must be within <b>{d.settings.radius_m} m</b> of PTG. Location is only checked while this page is open — the website cannot track you in the background after it is closed.</> : <>Admin has not set the PTG office location yet.</>}</p>
      </div>
      {ld ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div> : (
        <>
          <h2 className="font-bold text-slate-900 mb-2">Today's shifts</h2>
          {(d.today || []).length === 0
            ? <p className="text-sm text-slate-500 mb-4">No shifts scheduled for you today. Please contact admin if this is unexpected.</p>
            : <div className="space-y-3" data-testid="scan-today">{d.today.map((s) => <ShiftCard key={s.id} shift={s} onDone={load} method="qr" strict={false} />)}</div>}
          <p className="text-xs text-slate-400 mt-4">Tap <b>Clock In</b> or <b>Clock Out</b> above and confirm — nothing is recorded automatically.</p>
        </>
      )}
    </Shell>
  );
}
